// admin-ui/src/pages/OrdersPage.tsx
// ALL RIGHTS RESERVED

/**
 * Exchange Orders Management Page
 * Admin interface for viewing, filtering, and managing exchange orders
 */

import { useState, useCallback } from 'react';
import { useAdminOrders, useExchangeStats } from '../hooks/useExchangeAdmin';
import { MetricCard } from '../components/MetricCard';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface OrderFilters {
  status?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  side?: string;
  minAmount?: string;
  maxAmount?: string;
}

type OrderStatus = 'pending' | 'submitted' | 'filled' | 'settled' | 'failed' | 'cancelled';

interface Order {
  id: string;
  userId: string;
  side: 'buy' | 'sell';
  status: OrderStatus;
  amountUsd: string;
  amountCxxr: string;
  feeAmount: string;
  netAmount: string;
  filledPrice: string | null;
  failureReason: string | null;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: '#FFC107',
  submitted: '#FFC107',
  filled: '#2196F3',
  settled: '#4CAF50',
  failed: '#F44336',
  cancelled: '#9E9E9E',
};

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export function OrdersPage() {
  const [filters, setFilters] = useState<OrderFilters>({});
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;
  
  const { stats } = useExchangeStats('24h');
  const { orders, total, isLoading, cancelOrder, retrySettlement, refetch } = useAdminOrders({
    ...filters,
    limit,
    offset: page * limit,
  });
  
  const handleFilterChange = useCallback((key: keyof OrderFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPage(0);
  }, []);
  
  const handleClearFilters = useCallback(() => {
    setFilters({});
    setPage(0);
  }, []);
  
  const handleCancelOrder = useCallback(async () => {
    if (!selectedOrder || !cancelReason.trim()) return;
    
    try {
      await cancelOrder(selectedOrder.id, cancelReason);
      setShowCancelModal(false);
      setCancelReason('');
      setSelectedOrder(null);
      refetch();
    } catch (error) {
      alert(`Failed to cancel order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [selectedOrder, cancelReason, cancelOrder, refetch]);
  
  const handleRetrySettlement = useCallback(async (order: Order) => {
    if (!confirm(`Retry settlement for order ${order.id}?`)) return;
    
    try {
      await retrySettlement(order.id);
      refetch();
    } catch (error) {
      alert(`Failed to retry settlement: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [retrySettlement, refetch]);
  
  const handleExportCSV = useCallback(() => {
    const headers = ['Order ID', 'User ID', 'Side', 'Status', 'Amount USD', 'Amount CXXR', 'Fee', 'Net', 'Created'];
    const rows = orders.map(o => [
      o.id,
      o.userId,
      o.side,
      o.status,
      o.amountUsd,
      o.amountCxxr,
      o.feeAmount,
      o.netAmount,
      o.createdAt,
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exchange-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [orders]);
  
  const totalPages = Math.ceil(total / limit);
  
  return (
    <div className="orders-page">
      <header className="page-header">
        <h1>Exchange Orders</h1>
        <div className="header-actions">
          <button onClick={handleExportCSV} className="secondary">Export CSV</button>
          <button onClick={() => refetch()}>Refresh</button>
        </div>
      </header>
      
      {/* Stats Row */}
      {stats && (
        <section className="metrics-row">
          <MetricCard
            title="Volume (24h)"
            value={formatCurrency(stats.volume)}
            subtitle={`${stats.orderCount} orders`}
          />
          <MetricCard
            title="Buy Orders"
            value={stats.buyCount.toString()}
            subtitle={formatCurrency(stats.buyVolume)}
          />
          <MetricCard
            title="Sell Orders"
            value={stats.sellCount.toString()}
            subtitle={formatCurrency(stats.sellVolume)}
          />
          <MetricCard
            title="Pending"
            value={stats.pendingCount.toString()}
            status={stats.pendingCount > 10 ? 'warning' : undefined}
          />
          <MetricCard
            title="Failed (24h)"
            value={stats.failedCount.toString()}
            status={stats.failedCount > 0 ? 'error' : undefined}
          />
        </section>
      )}
      
      {/* Filters */}
      <section className="filters-panel">
        <div className="filter-row">
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="filled">Filled</option>
            <option value="settled">Settled</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          
          <select
            value={filters.side || ''}
            onChange={(e) => handleFilterChange('side', e.target.value)}
          >
            <option value="">All Sides</option>
            <option value="buy">Buy</option>
            <option value="sell">Sell</option>
          </select>
          
          <input
            type="text"
            placeholder="User ID"
            value={filters.userId || ''}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
          />
          
          <input
            type="date"
            placeholder="Start Date"
            value={filters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
          
          <input
            type="date"
            placeholder="End Date"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
          
          <button onClick={handleClearFilters} className="text-button">
            Clear Filters
          </button>
        </div>
      </section>
      
      {/* Orders Table */}
      <section className="panel">
        {isLoading ? (
          <div className="loading">Loading orders...</div>
        ) : (
          <>
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>User</th>
                  <th>Side</th>
                  <th>Amount</th>
                  <th>Fee</th>
                  <th>Net</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="mono">{order.id.slice(-12)}</td>
                    <td className="mono">{order.userId}</td>
                    <td>
                      <span className={`side-badge ${order.side}`}>
                        {order.side.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {order.side === 'buy' 
                        ? formatCurrency(order.amountUsd)
                        : `${parseFloat(order.amountCxxr).toFixed(2)} CXXR`
                      }
                    </td>
                    <td>{formatCurrency(order.feeAmount)}</td>
                    <td>
                      {order.side === 'buy'
                        ? `${parseFloat(order.netAmount).toFixed(2)} CXXR`
                        : formatCurrency(order.netAmount)
                      }
                    </td>
                    <td>
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: STATUS_COLORS[order.status] + '20', color: STATUS_COLORS[order.status] }}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td className="actions">
                      <button 
                        className="text-button"
                        onClick={() => setSelectedOrder(order)}
                      >
                        View
                      </button>
                      {order.status === 'pending' && (
                        <button
                          className="text-button danger"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowCancelModal(true);
                          }}
                        >
                          Cancel
                        </button>
                      )}
                      {order.status === 'failed' && (
                        <button
                          className="text-button warning"
                          onClick={() => handleRetrySettlement(order)}
                        >
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            <div className="pagination">
              <span>
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </span>
              <div className="pagination-buttons">
                <button 
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>
      
      {/* Order Detail Modal */}
      {selectedOrder && !showCancelModal && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Order Details</h2>
            <div className="order-details">
              <div className="detail-row">
                <label>Order ID</label>
                <span className="mono">{selectedOrder.id}</span>
              </div>
              <div className="detail-row">
                <label>User ID</label>
                <span className="mono">{selectedOrder.userId}</span>
              </div>
              <div className="detail-row">
                <label>Type</label>
                <span>{selectedOrder.side === 'buy' ? 'Buy CXXR' : 'Sell CXXR'}</span>
              </div>
              <div className="detail-row">
                <label>Status</label>
                <span 
                  className="status-badge"
                  style={{ backgroundColor: STATUS_COLORS[selectedOrder.status] + '20', color: STATUS_COLORS[selectedOrder.status] }}
                >
                  {selectedOrder.status}
                </span>
              </div>
              <div className="detail-row">
                <label>Amount USD</label>
                <span>{formatCurrency(selectedOrder.amountUsd)}</span>
              </div>
              <div className="detail-row">
                <label>Amount CXXR</label>
                <span>{parseFloat(selectedOrder.amountCxxr).toFixed(2)} CXXR</span>
              </div>
              <div className="detail-row">
                <label>Fee</label>
                <span>{formatCurrency(selectedOrder.feeAmount)}</span>
              </div>
              <div className="detail-row">
                <label>Net Amount</label>
                <span>{formatCurrency(selectedOrder.netAmount)}</span>
              </div>
              <div className="detail-row">
                <label>Created</label>
                <span>{formatDate(selectedOrder.createdAt)}</span>
              </div>
              {selectedOrder.failureReason && (
                <div className="detail-row">
                  <label>Failure Reason</label>
                  <span className="error">{selectedOrder.failureReason}</span>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button onClick={() => setSelectedOrder(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Cancel Modal */}
      {showCancelModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Cancel Order</h2>
            <p>Order ID: {selectedOrder.id}</p>
            <textarea
              placeholder="Enter cancellation reason..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
            <div className="modal-actions">
              <button className="secondary" onClick={() => setShowCancelModal(false)}>
                Back
              </button>
              <button 
                className="danger"
                onClick={handleCancelOrder}
                disabled={!cancelReason.trim()}
              >
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
